// Abas Indicadores, Antes de começar e marcos, e Pessoas.
import { useState } from 'react';
import { Button, Form, Input, Modal, Select, Table, Tag, Typography } from 'antd';
import { salvarIndicador, salvarMarco, salvarPapel, type Indicador, type Marco, type Papel } from './api';
import { CampoData, type Contexto } from './comum';
import InformacoesTabela from './InformacoesTabela';
import { fmtDataBr } from '../../utils/dataFmt';
import { dia } from './regras';

const { Text } = Typography;

export function AbaIndicadores({ ctx }: { ctx: Contexto }) {
  const { dados, salvar } = ctx;
  const gestor = dados.permissoes.pode_gerir;
  const [edit, setEdit] = useState<Indicador | null>(null);
  const [v, setV] = useState({ meta: '', valor_atual: '', leitura_em: null as string | null });
  const [gravando, setGravando] = useState(false);

  const abrir = (i: Indicador) => { setEdit(i); setV({ meta: i.meta || '', valor_atual: i.valor_atual || '', leitura_em: dia(i.leitura_em) }); };
  const gravar = async () => {
    if (!edit) return;
    setGravando(true);
    const ok = await salvar(() => salvarIndicador(edit.id, edit.revisao, {
      meta: v.meta.trim() || null, valor_atual: v.valor_atual.trim() || null, leitura_em: v.leitura_em,
    }), 'Indicador salvo.');
    setGravando(false);
    if (ok) setEdit(null);
  };

  return (
    <>
      <Table<Indicador>
        size="small" pagination={false} rowKey="id" dataSource={dados.indicadores} scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Sem indicadores neste ciclo.' }}
        columns={[
          {
            title: 'Indicador', dataIndex: 'nome',
            render: (_, i) => (
              <div>
                <div>{i.nome} {i.fonte === 'automatico' && <Tag>Automático</Tag>}</div>
                {i.como_mede && <Text type="secondary" style={{ fontSize: 12 }}>Como medir: {i.como_mede}</Text>}
              </div>
            ),
          },
          {
            title: 'Começo do ciclo', width: 240,
            render: (_, i) => (
              <div>
                <Text strong>{i.linha_base || '—'}</Text>
                {i.linha_base_nota && <div style={{ fontSize: 12, color: '#8c8c8c' }}>{i.linha_base_nota}</div>}
              </div>
            ),
          },
          { title: 'Meta', width: 140, render: (_, i) => i.meta || <Text type="secondary">a definir</Text> },
          { title: 'Agora', width: 140, render: (_, i) => i.valor_atual || '—' },
          { title: 'Lido em', width: 110, render: (_, i) => fmtDataBr(i.leitura_em) },
          { title: '', width: 80, render: (_, i) => (gestor ? <Button type="link" size="small" onClick={() => abrir(i)}>Editar</Button> : null) },
        ]}
      />
      <Modal
        open={!!edit} title={edit?.nome} onCancel={() => setEdit(null)} onOk={gravar} okText="Salvar" cancelText="Cancelar"
        confirmLoading={gravando} width="94vw" style={{ maxWidth: 640, top: 16 }} destroyOnHidden
      >
        <Form layout="vertical">
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Form.Item label="Meta"><Input value={v.meta} onChange={(e) => setV({ ...v, meta: e.target.value })} /></Form.Item>
            <Form.Item label="Valor agora"><Input value={v.valor_atual} onChange={(e) => setV({ ...v, valor_atual: e.target.value })} /></Form.Item>
            <Form.Item label="Lido em"><CampoData valor={v.leitura_em} onChange={(d) => setV({ ...v, leitura_em: d })} /></Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
}

export function AbaAntesEMarcos({ ctx }: { ctx: Contexto }) {
  const { dados, salvar } = ctx;
  const gestor = dados.permissoes.pode_gerir;
  const [edit, setEdit] = useState<Marco | null>(null);
  const [v, setV] = useState({ data_prevista: null as string | null, feito_em: null as string | null, observacao: '' });
  const [gravando, setGravando] = useState(false);

  const abrir = (m: Marco) => { setEdit(m); setV({ data_prevista: dia(m.data_prevista), feito_em: dia(m.feito_em), observacao: m.observacao || '' }); };
  const gravar = async () => {
    if (!edit) return;
    setGravando(true);
    const ok = await salvar(() => salvarMarco(edit.id, edit.revisao, {
      data_prevista: v.data_prevista, feito_em: v.feito_em, observacao: v.observacao.trim() || null,
    }), 'Marco salvo.');
    setGravando(false);
    if (ok) setEdit(null);
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div>
        <Text strong>Antes de começar</Text>
        <div style={{ marginTop: 6 }}><InformacoesTabela itens={dados.antes} ctx={ctx} /></div>
      </div>
      <div>
        <Text strong>Marcos com a consultoria</Text>
        <Table<Marco>
          size="small" pagination={false} rowKey="id" dataSource={dados.marcos} style={{ marginTop: 6 }} scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Sem marcos neste ciclo.' }}
          columns={[
            { title: 'Marco', dataIndex: 'titulo' },
            { title: 'Previsto para', width: 130, render: (_, m) => fmtDataBr(m.data_prevista) },
            { title: 'Feito em', width: 130, render: (_, m) => (m.feito_em ? fmtDataBr(m.feito_em) : <Text type="secondary">ainda não</Text>) },
            { title: 'Observação', dataIndex: 'observacao', render: (o: string | null) => o || '—' },
            { title: '', width: 80, render: (_, m) => (gestor ? <Button type="link" size="small" onClick={() => abrir(m)}>Editar</Button> : null) },
          ]}
        />
      </div>
      <Modal
        open={!!edit} title={edit?.titulo} onCancel={() => setEdit(null)} onOk={gravar} okText="Salvar" cancelText="Cancelar"
        confirmLoading={gravando} width="94vw" style={{ maxWidth: 640, top: 16 }} destroyOnHidden
      >
        <Form layout="vertical">
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Form.Item label="Previsto para"><CampoData valor={v.data_prevista} onChange={(d) => setV({ ...v, data_prevista: d })} /></Form.Item>
            <Form.Item label="Feito em"><CampoData valor={v.feito_em} onChange={(d) => setV({ ...v, feito_em: d })} /></Form.Item>
          </div>
          <Form.Item label="Observação"><Input.TextArea rows={3} value={v.observacao} onChange={(e) => setV({ ...v, observacao: e.target.value })} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export function AbaPessoas({ ctx }: { ctx: Contexto }) {
  const { dados, salvar, contas } = ctx;
  const gestor = dados.permissoes.pode_gerir;
  const cicloId = dados.ciclo.id;

  const opcoes = (p: Papel) => {
    const base = contas.map((c) => ({ value: c.email.toLowerCase(), label: `${c.nome || c.email} — ${c.email}` }));
    const atual = (p.email || '').toLowerCase();
    if (atual && !base.some((o) => o.value === atual)) base.unshift({ value: atual, label: `${p.nome || atual} — ${atual}` });
    return base;
  };

  return (
    <Table<Papel>
      size="small" pagination={false} rowKey="papel" dataSource={dados.papeis} scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'Sem papéis neste ciclo.' }}
      columns={[
        { title: 'Papel no ciclo', dataIndex: 'rotulo', width: 240 },
        {
          title: 'Quem é',
          render: (_, p) => (gestor
            ? (
              <Select
                style={{ width: '100%', minWidth: 280 }} showSearch optionFilterProp="label" options={opcoes(p)}
                value={p.email ? p.email.toLowerCase() : undefined} placeholder="Escolha a pessoa"
                onChange={(email) => { void salvar(() => salvarPapel(cicloId, p.papel, email), 'Pessoa trocada.'); }}
              />
            )
            : (p.nome ? `${p.nome} — ${p.email}` : (p.email || <Text type="secondary">sem pessoa</Text>))),
        },
      ]}
    />
  );
}
